import { Button, Empty, Row, Select, SelectProps, Skeleton, Typography, theme } from 'antd';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GrispiInputProps } from '../..';
import useAutocompleteSearchByFieldKey from '../../../../hooks/autocomplete/useAutocompleteSearchByFieldKey';
import useAutocompleteSuggestionsByFieldKey from '../../../../hooks/autocomplete/useAutocompleteSuggestionsByFieldKey';
import useFieldOptionsByIds from '../../../../hooks/autocomplete/useFieldOptionsByIds';
import useDebounce from '../../../../hooks/useDebounce';
import { CustomFieldOption } from '../../../../model/types';
import { isPublicPage } from '../../../../utils/routeHelper';
import CustomDivider from '../../../CustomDivider';
import LoadingMenu from '../../../LoadingMenu';
import GrispiInputContainer from '../../GrispiInputContainer';
import { formatOptionIdToValue } from '../GrispiSelectInput/GrispiAutocompleteSelectInput';

type GrispiMultiSelectInputProps = Omit<GrispiInputProps<'MULTI_SELECT'>, 'type'>;

const DynamicOptionModal = dynamic(() => import('../../../AddOptionModal/index'));

const initialSearchTerm = '';
const transformFormValue = (value: string) => {
    if (!value) return;
    return value.split(',');
};
const GrispiAutocompleteMultiSelectInput = ({
    antdProps,
    containerProps,
    isNewValueAllowed,
    onChange,
    options,
    value,
    fieldKey,
    ...rest
}: GrispiMultiSelectInputProps) => {
    const { t } = useTranslation('forms');
    const {
        token: { colorTextLightSolid, marginXXS }
    } = theme.useToken();
    const { ref } = antdProps;
    const { pathname } = useRouter();

    const isPublic = isPublicPage(pathname);

    const selectRef = useRef<HTMLSelectElement>(ref);
    const addButtonRef = useRef<HTMLButtonElement>();
    const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
    const [fieldOptionList, setFieldOptionList] = useState<CustomFieldOption[]>(null);

    const {
        data: optionsData,
        isLoading: isLoadingOptions,
        isValidating
    } = useFieldOptionsByIds(fieldKey, value?.toString(), isPublic);
    const fieldOptions = useMemo(() => formatOptionIdToValue(optionsData?.result), [optionsData?.result]);

    useEffect(() => {
        if (!isLoadingOptions && !isValidating) {
            setFieldOptionList(fieldOptions);
        }
    }, [fieldOptions, isLoadingOptions, isValidating]);

    const {
        data: searchData,
        isLoading: isLoadingSearch,
        mutate: mutateSearch
    } = useAutocompleteSearchByFieldKey(fieldKey, debouncedSearchTerm, isPublic);
    const searchResult = useMemo(() => formatOptionIdToValue(searchData?.result), [searchData?.result]);

    const {
        data: suggestions,
        setIsSuggestionRequested,
        isLoading: isLoadingSuggestions,
        mutate: mutateSuggestions
    } = useAutocompleteSuggestionsByFieldKey(fieldKey, !searchData?.result?.length, isPublic);
    const suggestionResult = useMemo(
        () => formatOptionIdToValue(suggestions?.result?.filter((op) => !fieldOptionList?.find((o) => o.id === op.id))),
        [fieldOptionList, suggestions?.result]
    );

    const onEnterKeyDown: SelectProps<string | string[]>['onInputKeyDown'] = ({ key }) => {
        if (key === 'Enter' && addButtonRef.current) addButtonRef.current.click();
    };
    const addItem = () => {
        setIsOptionModalOpen(true);
    };
    const resetSearch = () => {
        setSearchTerm(initialSearchTerm);
    };

    const isLoading = isLoadingOptions || isLoadingSearch || isLoadingSuggestions;
    const isPreparingToSearch = !!debouncedSearchTerm && !searchResult && !isLoadingSearch;

    const notFoundContent =
        isLoading || isPreparingToSearch ? (
            <LoadingMenu />
        ) : debouncedSearchTerm === initialSearchTerm ? (
            <Row justify="center">
                <Typography.Text style={{ marginBottom: marginXXS }}> {t('autocomplete.empty')}</Typography.Text>
            </Row>
        ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
                {isNewValueAllowed && debouncedSearchTerm && !isLoadingSearch ? (
                    <Button type="primary" onClick={addItem} ref={addButtonRef} className="full-width">
                        <Typography.Text ellipsis style={{ color: colorTextLightSolid }} className="full-width">
                            {`${t('addOption')} "${debouncedSearchTerm}"`}
                        </Typography.Text>
                    </Button>
                ) : null}
            </Empty>
        );

    const preOptions = formatOptionIdToValue(options as CustomFieldOption[]);

    const finalOptions = useMemo(() => {
        return isLoadingSearch || isPreparingToSearch
            ? []
            : searchResult
            ? [...searchResult]
            : suggestionResult && fieldOptionList && preOptions
            ? [...preOptions, ...fieldOptionList, ...suggestionResult]
            : fieldOptionList && preOptions
            ? [...preOptions, ...fieldOptionList]
            : suggestionResult && preOptions
            ? [...preOptions, ...suggestionResult]
            : suggestionResult && fieldOptionList
            ? [...fieldOptionList, ...suggestionResult]
            : fieldOptionList
            ? [...fieldOptionList]
            : suggestionResult
            ? [...suggestionResult]
            : preOptions
            ? [...preOptions]
            : [];
    }, [isLoadingSearch, isPreparingToSearch, searchResult, suggestionResult, fieldOptionList, preOptions]);

    const handleOnSelect = (id: string) => {
        setFieldOptionList((prev) => [...(prev || []), finalOptions?.find((v) => v.value === id)]);
        resetSearch();
        selectRef.current?.blur();
    };
    const isSuggestionTextRender = !debouncedSearchTerm && !searchResult?.length && !!finalOptions?.length;
    const isNoResultTextRender =
        !!debouncedSearchTerm && Array.isArray(searchResult) && !searchResult?.length && !isLoading;
    const isSuggestionLoadingRender = !isLoadingSearch && !!preOptions?.length && isLoadingSuggestions;
    if (fieldOptionList === null)
        return (
            <GrispiInputContainer {...containerProps}>
                <Skeleton.Input active className="full-width" />
            </GrispiInputContainer>
        );
    return (
        <>
            <GrispiInputContainer {...containerProps}>
                <Select<string | string[]>
                    {...antdProps}
                    allowClear
                    showSearch
                    tokenSeparators={[',']} // only applies when mode='tags'
                    mode="multiple"
                    onChange={(v) => onChange(v?.toString())}
                    options={finalOptions}
                    filterOption={false}
                    value={transformFormValue(value)}
                    notFoundContent={notFoundContent}
                    onDropdownVisibleChange={(visible) => {
                        setIsSuggestionRequested(visible);
                        resetSearch();
                    }}
                    dropdownRender={(option) => (
                        <>
                            {isSuggestionTextRender && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS }}>
                                        {t('autocomplete.empty')}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            {isNoResultTextRender && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS }}>
                                        {t('autocomplete.noResults')}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            {option}
                            {isSuggestionLoadingRender && <LoadingMenu />}
                        </>
                    )}
                    onSearch={setSearchTerm}
                    onSelect={handleOnSelect}
                    onDeselect={resetSearch}
                    onInputKeyDown={onEnterKeyDown}
                    ref={selectRef}
                    {...rest}
                />
            </GrispiInputContainer>
            {isNewValueAllowed && isOptionModalOpen && (
                <DynamicOptionModal
                    isOpen={isOptionModalOpen}
                    closeModal={() => setIsOptionModalOpen(false)}
                    optionValue={debouncedSearchTerm}
                    setOptionValue={async (option) => {
                        if (!option.id || !option.label) return;
                        if (value) value = value + ',' + option.id?.toString();
                        else value = option.id?.toString();
                        setFieldOptionList((prev) => [...(prev || []), { ...option, value: option.id?.toString() }]);
                        await mutateSearch();
                        await mutateSuggestions();
                        onChange(value);
                        resetSearch();
                    }}
                    fieldKey={fieldKey}
                    fieldName={containerProps?.label?.toString()}
                />
            )}
        </>
    );
};

export default GrispiAutocompleteMultiSelectInput;
